import {DataContentWrapper} from '@hconnect/common/containers'
import {useUserContext} from '@hconnect/login'
import {useUpmPlantContext} from '@hconnect/upm'
import {Modal, ModalContent, ModalTitle} from '@hm/fundamental'
import {usePermission} from '@hproduce/permissions'

import {CommentsVersionsView} from '../components'
import {
  useAddSlaKpiComment,
  useEditSlaKpiComment,
  useDeleteSlaKpiComment,
  useSlaKpiTabValueEvents
} from '../hooks'

type Props = {
  isOpen: boolean
  kpiName: string
  kpiUnit: string
  kpiValueId: string
  onClose: () => void
}

export const SlaKpiCommentsVersionsContainer = ({
  isOpen,
  onClose,
  kpiName,
  kpiUnit,
  kpiValueId
}: Props) => {
  const {upmId: plantUpmId, plantId, country: countryId} = useUpmPlantContext()
  const {loggedUser} = useUserContext()
  const {
    data = [],
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useSlaKpiTabValueEvents(
    {plantUpmId, kpiValueId},
    {
      enabled: isOpen
    }
  )
  const {mutateAsync: addCommentAsync} = useAddSlaKpiComment({plantUpmId, kpiValueId})
  const {mutateAsync: editCommentAsync} = useEditSlaKpiComment({plantUpmId, kpiValueId})
  const {mutateAsync: deleteCommentAsync} = useDeleteSlaKpiComment({plantUpmId, kpiValueId})

  const canAddComment = usePermission({
    permissionTypes: ['EDIT_SLA_KPIS'],
    plantContext: {plantId, countryId}
  })

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Modal open={isOpen} onClose={onClose} data-cy="sla-kpi-comments-versions-modal">
        <ModalTitle>
          {kpiName} - {kpiUnit}
        </ModalTitle>
        <ModalContent className="max-h-[max(80vh,500px)] overflow-y-auto">
          <DataContentWrapper
            showLoader={isLoading}
            data={data}
            isError={isError}
            isRefetching={isRefetching}
            retryFunction={() => void refetch()}
            progressSx={{color: 'primary.main'}}
          >
            <CommentsVersionsView
              kpiValueEvents={data}
              canAddComment={canAddComment}
              loggedUserId={loggedUser?.id}
              onAddComment={async (value) => {
                await addCommentAsync({value})
              }}
              onEditComment={async (commentId, commentText) => {
                await editCommentAsync({commentId, commentText})
              }}
              onDeleteComment={async (commentId) => {
                await deleteCommentAsync({commentId})
              }}
            />
          </DataContentWrapper>
        </ModalContent>
      </Modal>
    </div>
  )
}
